import { useEffect } from 'react';
import { DevTool } from '@hookform/devtools';
import { Employer } from 'services/requests';
import { useAddEmployer } from 'hooks/api/employer';
import {
  EmployerFieldNames,
  defaultValues,
  employerTypeOptions,
  useEmployerForm,
} from 'hooks/forms';
import { useNavigate } from 'hooks/routes';
import { useEditEmployer } from 'hooks/api/employer/useEditEmployer';
import { Modal, ModalProps } from 'components/Modal';
import Field from 'components/Field';
import Button from 'components/Button';
import Picker from 'components/Picker';

import messages from './EmployersModal.messages';
import styles from './EmployersModal.module.css';

type Props = {
  employer?: Employer;
  onRequestClose: VoidFunction;
} & ModalProps;

const EmployersModal = ({
  employer,
  onRequestClose,
  ...restOfProps
}: Props): JSX.Element | null => {
  const { mutateAsync: createEmployer, isLoading: isCreateLoading } = useAddEmployer();
  const { mutateAsync: editEmployer, isLoading: isEditLoading } = useEditEmployer();
  const { toIraUsersRoleAdmin } = useNavigate();

  const isEditMode = Boolean(employer);

  const form = useEmployerForm(employer);

  useEffect(() => {
    if (!isEditMode) {
      form.reset(defaultValues);
    }
  }, [isEditMode, restOfProps.isOpen]);

  const onSubmit = async (): Promise<void> => {
    const payload = form.getValues();

    if (employer) {
      await editEmployer(
        {
          payload: {
            name: payload.name,
          },
          id: employer.id.toString(),
        },
        {
          onSuccess: () => onRequestClose(),
        },
      );
      return;
    }

    await createEmployer(
      {
        name: payload.name,
      },
      {
        onSuccess: (data) => {
          onRequestClose();
          toIraUsersRoleAdmin(data.id.toString());
        },
      },
    );
  };

  const title: string = isEditMode ? messages.titleEdit : messages.titleAdd;
  const subTitle: string = messages.subTitleAddAndEdit;
  const buttonText: string = isEditMode ? messages.buttonAddEdit : messages.buttonAddAdd;

  const isLoading = isCreateLoading || isEditLoading;

  return (
    <Modal
      {...restOfProps}
      onRequestClose={onRequestClose}
      className={styles.employersModal}
      disabled={isLoading}
    >
      <DevTool control={form.control} />
      <h3 className={styles.employersModal__title}>{title}</h3>
      <p className={styles.employersModal__text}>{subTitle}</p>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Field
          type={'text'}
          form={form}
          name={EmployerFieldNames.name}
          placeholder={messages.placeholder.name}
          className={styles.employersModal__input}
        />

        <Picker
          form={form}
          name={EmployerFieldNames.type}
          options={[employerTypeOptions.multipleEmployeesType]}
          placeholder={messages.placeholder.type}
          className={styles.employersModal__dropdown}
          disabled={true}
          size={'large'}
        />

        <div className={styles.employersModal__buttons}>
          <Button variant={'outline'} onClick={onRequestClose} type={'button'}>
            {messages.buttonCancel}
          </Button>
          <Button type={'submit'} isLoading={isLoading}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EmployersModal;
